import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { rePage } from "@/lib/docstate";
import { EnquiryForm } from "@/components/docstate/EnquiryForm";

export default function DocstatePage({ slug, withEnquiry = false, enquiryKind = "general", enquiryHeading }) {
  const [page, setPage] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setPage(null);
    setMissing(false);
    rePage(slug).then(setPage).catch(() => setMissing(true));
  }, [slug]);

  if (missing)
    return (
      <div className="ds-bg flex min-h-screen items-center justify-center pt-16">
        <p className="ds-display text-xl text-[#F3E5AB]">This page is being prepared.</p>
      </div>
    );
  if (!page)
    return (
      <div className="ds-bg flex min-h-screen items-center justify-center pt-16">
        <Loader2 className="h-8 w-8 animate-spin text-[#C9A24B]" />
      </div>
    );

  return (
    <div className="ds-bg min-h-screen" data-testid={`docstate-page-${slug}`}>
      {/* Hero */}
      <section className="relative flex min-h-[46vh] items-end overflow-hidden pt-16">
        {page.hero_image && (
          <img src={page.hero_image} alt={page.title} className="absolute inset-0 h-full w-full object-cover opacity-45" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0C] via-[#0B0B0C]/70 to-transparent" />
        <div className="relative mx-auto w-full max-w-7xl px-4 pb-10 sm:px-8 lg:px-12">
          {page.subtitle && <p className="ds-eyebrow">{page.subtitle}</p>}
          <h1 className="ds-display mt-2 text-4xl font-medium text-white sm:text-5xl">{page.title}</h1>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-8 lg:px-12">
        {page.intro && <p className="max-w-3xl text-lg font-light leading-relaxed text-[#C9BBB2]">{page.intro}</p>}

        {Array.isArray(page.stats) && page.stats.length > 0 && (
          <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3" data-testid="page-stats">
            {page.stats.map((s, i) => (
              <div key={i} className="ds-card p-6 text-center">
                <p className="ds-display text-3xl text-[#F3E5AB]">{s.value}</p>
                <p className="mt-1 text-xs uppercase tracking-widest text-[#9D8D83]">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        {Array.isArray(page.blocks) && page.blocks.length > 0 && (
          <div className="mt-14 space-y-12">
            {page.blocks.map((b, i) => (
              <div key={i} className={`grid items-center gap-8 lg:grid-cols-2 ${i % 2 ? "lg:[&>*:first-child]:order-2" : ""}`}>
                {b.image ? (
                  <img src={b.image} alt={b.heading} className="h-64 w-full rounded-xl object-cover" />
                ) : (
                  <div className="hidden lg:block" />
                )}
                <div>
                  <h2 className="ds-display text-2xl text-white">{b.heading}</h2>
                  <p className="mt-3 font-light leading-relaxed text-[#C9BBB2]">{b.body}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {withEnquiry && (
          <div id="enquiry" className="mx-auto mt-16 max-w-2xl scroll-mt-24">
            <EnquiryForm kind={enquiryKind} subject={page.title} heading={enquiryHeading || page.cta_label || "Send an enquiry"} />
          </div>
        )}
      </div>
    </div>
  );
}
